import * as THREE from 'three';
import * as Tone from 'tone';

const MAX_PARTICLES = 5000;
const PARTICLES_PER_NOTE = 60;

const vertexShader = `
  attribute float size;
  attribute float alpha;
  attribute vec3 color;
  varying float vAlpha;
  varying vec3 vColor;

  void main() {
    vAlpha = alpha;
    vColor = color;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (150.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const fragmentShader = `
  varying float vAlpha;
  varying vec3 vColor;

  void main() {
    float dist = length(gl_PointCoord - vec2(0.5));
    if (dist > 0.5) discard;
    float glow = exp(-dist * 6.0) * 0.8 + smoothstep(0.5, 0.0, dist) * 0.2;
    gl_FragColor = vec4(vColor, glow * vAlpha);
  }
`;

export class ParticleSystem {
  constructor(scene) {
    this._positions = new Float32Array(MAX_PARTICLES * 3);
    this._velocities = new Float32Array(MAX_PARTICLES * 3);
    this._sizes = new Float32Array(MAX_PARTICLES);
    this._alphas = new Float32Array(MAX_PARTICLES);
    this._colors = new Float32Array(MAX_PARTICLES * 3);
    this._lifetimes = new Float32Array(MAX_PARTICLES);
    this._ages = new Float32Array(MAX_PARTICLES);
    this._activeCount = 0;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this._positions, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(this._sizes, 1));
    geometry.setAttribute('alpha', new THREE.BufferAttribute(this._alphas, 1));
    geometry.setAttribute('color', new THREE.BufferAttribute(this._colors, 3));

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this._points = new THREE.Points(geometry, material);
    this._geometry = geometry;
    scene.add(this._points);
  }

  emit(note) {
    const midi = Tone.Frequency(note).toMidi();
    const normalizedNote = (midi - 36) / 60; // ~C2 to ~C7

    // Color based on pitch: low = cyan, mid = magenta, high = purple/white
    let r, g, b;
    if (normalizedNote < 0.33) {
      r = 0; g = 1; b = 0.95; // cyan
    } else if (normalizedNote < 0.66) {
      r = 1; g = 0; b = 0.9; // magenta
    } else {
      r = 0.7; g = 0.5; b = 1; // purple-white
    }

    // Random spawn position in view
    const cx = (Math.random() - 0.5) * 40;
    const cy = (Math.random() - 0.5) * 20;
    const cz = -30 - Math.random() * 20;

    for (let i = 0; i < PARTICLES_PER_NOTE; i++) {
      const idx = this._activeCount % MAX_PARTICLES;

      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 0.3 + 0.1;
      const elevation = (Math.random() - 0.5) * Math.PI;

      this._positions[idx * 3] = cx;
      this._positions[idx * 3 + 1] = cy;
      this._positions[idx * 3 + 2] = cz;

      this._velocities[idx * 3] = Math.cos(angle) * Math.cos(elevation) * speed;
      this._velocities[idx * 3 + 1] = Math.sin(elevation) * speed;
      this._velocities[idx * 3 + 2] = Math.sin(angle) * Math.cos(elevation) * speed;

      this._sizes[idx] = Math.random() * 3 + 1;
      this._alphas[idx] = 1;
      this._colors[idx * 3] = r + (Math.random() - 0.5) * 0.2;
      this._colors[idx * 3 + 1] = g + (Math.random() - 0.5) * 0.2;
      this._colors[idx * 3 + 2] = b + (Math.random() - 0.5) * 0.2;
      this._lifetimes[idx] = 2 + Math.random() * 1.5;
      this._ages[idx] = 0;

      this._activeCount++;
    }

    this._geometry.attributes.position.needsUpdate = true;
    this._geometry.attributes.size.needsUpdate = true;
    this._geometry.attributes.alpha.needsUpdate = true;
    this._geometry.attributes.color.needsUpdate = true;
    this._geometry.setDrawRange(0, Math.min(this._activeCount, MAX_PARTICLES));
  }

  update(dt) {
    const count = Math.min(this._activeCount, MAX_PARTICLES);
    for (let i = 0; i < count; i++) {
      this._ages[i] += dt;
      const life = this._ages[i] / this._lifetimes[i];

      if (life >= 1) {
        this._alphas[i] = 0;
        continue;
      }

      // Move
      this._positions[i * 3] += this._velocities[i * 3];
      this._positions[i * 3 + 1] += this._velocities[i * 3 + 1];
      this._positions[i * 3 + 2] += this._velocities[i * 3 + 2];

      // Slight gravity
      this._velocities[i * 3 + 1] -= 0.001;

      // Fade out
      this._alphas[i] = 1 - life * life;

      // Shrink
      this._sizes[i] *= 0.998;
    }

    this._geometry.attributes.position.needsUpdate = true;
    this._geometry.attributes.alpha.needsUpdate = true;
    this._geometry.attributes.size.needsUpdate = true;
  }
}
